/**
 * Adjacency Map Sync Service (WQ-210)
 *
 * Wraps three existing WQ-204 endpoints to power the sync status chip:
 * - GET /api/admin/adjacency-maps/{map_id}/sharepoint-config
 *     → { enabled, interval_minutes, site_id, list_id,
 *         last_scheduled_sync, last_heartbeat_log }
 * - GET /api/admin/adjacency-maps/{map_id}/sync/history?limit=N
 *     → { map_id, count, runs: SyncHistoryEntry[] }
 * - GET /api/admin/scheduler/health
 *     → { running, job_count, healthy, jobs: SchedulerJob[] }
 *
 * All adjacency endpoints use `?tenant_id=...` (snake_case, matching
 * the Python parameter name — WQ-207 standardized this across the
 * Graph admin surface).
 */

export interface SharePointConfigResponse {
  enabled: boolean;
  interval_minutes: number;
  site_id: string | null;
  list_id: string | null;
  last_scheduled_sync: string | null;
  last_heartbeat_log: string | null;
}

export interface SyncHistoryEntry {
  id: string;
  status: string | null;
  pulled: number;
  pushed: number;
  conflicts: number;
  skipped: number;
  errors: string[];
  duration_ms: number | null;
  error_message: string | null;
  created_at: string | null;
}

export interface SyncHistoryResponse {
  map_id: string;
  count: number;
  runs: SyncHistoryEntry[];
}

export interface SchedulerJobState {
  id: string;
  name: string;
  trigger: string;
  next_run_time: string | null;
  seconds_until_next_run: number | null;
}

export interface SchedulerHealthResponse {
  running: boolean;
  job_count: number;
  healthy: boolean;
  jobs: SchedulerJobState[];
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

function tenantQs(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
}

export async function getSharePointConfig(
  authFetch: FetchFn,
  mapId: string,
  tenantId?: string | null,
): Promise<SharePointConfigResponse> {
  const res = await authFetch(
    `/api/admin/adjacency-maps/${encodeURIComponent(mapId)}/sharepoint-config${tenantQs(tenantId)}`,
  );
  if (!res.ok) {
    throw new Error(`SharePoint config load failed: ${res.status}`);
  }
  return res.json();
}

export async function getSyncHistory(
  authFetch: FetchFn,
  mapId: string,
  tenantId?: string | null,
  limit: number = 10,
): Promise<SyncHistoryEntry[]> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (tenantId) qs.set('tenantId', tenantId);
  const res = await authFetch(
    `/api/admin/adjacency-maps/${encodeURIComponent(mapId)}/sync/history?${qs.toString()}`,
  );
  if (!res.ok) {
    throw new Error(`Sync history load failed: ${res.status}`);
  }
  const body: SyncHistoryResponse | SyncHistoryEntry[] = await res.json();
  // Endpoint returns { map_id, count, runs: [...] }. Handle bare-array
  // for forward compatibility with any future shape simplification.
  if (Array.isArray(body)) return body;
  return body.runs ?? [];
}

export async function getSchedulerSharePointJob(
  authFetch: FetchFn,
): Promise<SchedulerJobState | null> {
  const res = await authFetch('/api/admin/scheduler/health');
  if (!res.ok) return null;
  const body: SchedulerHealthResponse = await res.json();
  const jobs = body.jobs ?? [];
  return jobs.find((j) => j.id === 'sharepoint_sync') ?? null;
}
